<script setup lang="ts">
import { computed } from 'vue'
import type { DebateResult } from '@/api/client'

const props = defineProps<{
  debateResult: DebateResult
}>()

const RATING_TAG: Record<string, { label: string; type: 'success' | 'warning' | 'danger' | 'info' }> = {
  Buy: { label: 'Buy · 强烈看多', type: 'success' },
  Overweight: { label: 'Overweight · 增持', type: 'success' },
  Hold: { label: 'Hold · 中性持有', type: 'info' },
  Underweight: { label: 'Underweight · 减持', type: 'warning' },
  Sell: { label: 'Sell · 强烈看空', type: 'danger' },
}

const SIDE_LABEL: Record<string, string> = {
  bull: '多头胜出',
  bear: '空头胜出',
  balanced: '势均力敌',
}

const ratingTag = computed(() => RATING_TAG[props.debateResult.rating] || { label: props.debateResult.rating, type: 'info' as const })
const sideLabel = computed(() => SIDE_LABEL[props.debateResult.deciding_side] || '-')
const bullScore = computed(() => props.debateResult.bull_view?.score ?? 0)
const bearScore = computed(() => props.debateResult.bear_view?.score ?? 0)
</script>

<template>
  <el-card shadow="never" class="debate-panel">
    <template #header>
      <div class="header-bar">
        <span class="title">⚔️ 多空辩论擂台 (ResearchManager 反中庸裁决)</span>
        <div class="header-tags">
          <el-tag :type="ratingTag.type" effect="dark" size="small">{{ ratingTag.label }}</el-tag>
          <el-tag type="info" effect="plain" size="small">决胜方: {{ sideLabel }}</el-tag>
          <el-tag v-if="debateResult.rounds_used" type="info" effect="plain" size="small">
            对抗 {{ debateResult.rounds_used }} 轮
          </el-tag>
        </div>
      </div>
    </template>

    <!-- 裁决理由 -->
    <div class="verdict-rationale">
      <strong>裁决理由:</strong> {{ debateResult.rationale || debateResult.consensus || '-' }}
    </div>

    <!-- 多空对抗擂台 -->
    <el-row :gutter="12" class="arena">
      <el-col :span="12">
        <div class="side side-bull">
          <div class="side-head">
            <span class="side-name">🟢 多头立论</span>
            <el-tag :type="bullScore >= bearScore ? 'success' : 'info'" effect="dark" size="small">
              评分 {{ bullScore }}/10
            </el-tag>
          </div>
          <p class="side-summary">{{ debateResult.bull_view?.summary || '-' }}</p>
          <ul v-if="debateResult.bull_view?.evidence?.length" class="side-evidence">
            <li v-for="(e, i) in debateResult.bull_view.evidence" :key="i">{{ e }}</li>
          </ul>
        </div>
      </el-col>
      <el-col :span="12">
        <div class="side side-bear">
          <div class="side-head">
            <span class="side-name">🔴 空头反驳</span>
            <el-tag :type="bearScore > bullScore ? 'danger' : 'info'" effect="dark" size="small">
              评分 {{ bearScore }}/10
            </el-tag>
          </div>
          <p class="side-summary">{{ debateResult.bear_view?.summary || '-' }}</p>
          <ul v-if="debateResult.bear_view?.evidence?.length" class="side-evidence">
            <li v-for="(e, i) in debateResult.bear_view.evidence" :key="i">{{ e }}</li>
          </ul>
        </div>
      </el-col>
    </el-row>

    <!-- 综合评分条 -->
    <div v-if="debateResult.overall_score" class="overall-score">
      综合评分 <strong>{{ debateResult.overall_score }}/10</strong>
    </div>

    <!-- 战略行动 -->
    <div v-if="debateResult.strategic_actions?.length" class="actions">
      <div class="sub-title">♟️ 战略行动</div>
      <el-tag
        v-for="(a, i) in debateResult.strategic_actions"
        :key="i"
        type="primary"
        effect="plain"
        size="small"
        class="action-tag"
      >
        {{ a }}
      </el-tag>
    </div>

    <!-- 未决分歧点 -->
    <div v-if="debateResult.disagreement_points?.length" class="disagreements">
      <div class="sub-title">⚡ 未决分歧点</div>
      <el-tag
        v-for="(d, i) in debateResult.disagreement_points"
        :key="i"
        type="warning"
        effect="plain"
        size="small"
        class="action-tag"
      >
        {{ d }}
      </el-tag>
    </div>
  </el-card>
</template>

<style scoped>
.debate-panel { display: flex; flex-direction: column; }
.header-bar { display: flex; justify-content: space-between; align-items: center; width: 100%; }
.header-tags { display: flex; gap: 6px; }
.title { font-size: 15px; font-weight: 600; }
.verdict-rationale {
  font-size: 13px; color: var(--text-primary); line-height: 1.6;
  background: var(--bg-tertiary); border-left: 3px solid var(--accent);
  padding: 8px 12px; border-radius: 0 6px 6px 0; margin-bottom: 12px;
}
.arena { margin-bottom: 10px; }
.side { border-radius: 8px; padding: 12px; height: 100%; border: 1px solid var(--border-color); }
.side-bull { background: var(--bg-success-subtle, rgba(52, 168, 83, 0.06)); }
.side-bear { background: var(--bg-danger-subtle, rgba(234, 67, 53, 0.06)); }
.side-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
.side-name { font-size: 14px; font-weight: 700; color: var(--text-primary); }
.side-summary { font-size: 13px; color: var(--text-primary); line-height: 1.6; margin: 0 0 6px; }
.side-evidence { padding-left: 18px; margin: 0; font-size: 12px; color: var(--text-secondary); line-height: 1.7; }
.overall-score { font-size: 13px; color: var(--text-secondary); margin-bottom: 10px; }
.sub-title { font-size: 13px; font-weight: 600; color: var(--text-primary); margin-bottom: 6px; }
.actions { margin-bottom: 10px; }
.action-tag { margin: 0 6px 6px 0; }
</style>
